import React, { useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useCompanyQuote } from "./hooks/useCompanyQuote";
import { useEarningsTranscript } from "./hooks/useEarningsTranscript";
import { useEarningsTranscriptDates } from "./hooks/useEarningsTranscriptDates";
// QuarterSubmenu no longer needed - using Select in hero
import TranscriptDisplay from "./components/TranscriptDisplay";
import { TranscriptMode } from "./components/TranscriptModeToggle";
import { QuarterOption } from "./utils/quarterUtils";

interface ViewCompanyEarningsTranscriptProps {
  symbol: string;
  year: number; // Calendar year (what user sees)
  quarter: number; // Calendar quarter (what user sees)
  mode: TranscriptMode;
  onStateChange: (updates: {
    year?: number;
    quarter?: number;
    mode?: TranscriptMode;
  }) => void;
  // Accept quarter options from parent to avoid duplicate fetching
  quarterOptions: QuarterOption[];
  selectedQuarter: string;
  onQuarterSelect: (quarterOption: QuarterOption) => void;
  isLoadingDates: boolean;
  fillHeight?: boolean;
}

const ViewCompanyEarningsTranscript: React.FC<
  ViewCompanyEarningsTranscriptProps
> = ({
  symbol,
  year,
  quarter,
  mode,
  onStateChange,
  quarterOptions,
  selectedQuarter,
  onQuarterSelect,
  isLoadingDates,
  fillHeight = true,
}) => {
  // Get transcript dates data for finding fiscal year/quarter
  const { data: transcriptDatesData, error: datesError } =
    useEarningsTranscriptDates(symbol);

  // Find the selected transcript date entry by matching calendar quarter
  const selectedTranscriptDate = useMemo(() => {
    if (!transcriptDatesData) return null;

    // Find the transcript date entry where the calculated calendar quarter matches selection
    return transcriptDatesData.find((item) => item.value === selectedQuarter);
  }, [transcriptDatesData, selectedQuarter]);

  // Convert fiscal data to strings for API calls (use original fiscal data from API)
  const fiscalYearStr = selectedTranscriptDate?.fiscalYear.toString();
  const fiscalQuarterStr = selectedTranscriptDate?.quarter.toString();

  // Fetch company profile data
  const { data: companyData } = useCompanyQuote(symbol);
  const companyName = Array.isArray(companyData)
    ? companyData[0]?.name
    : companyData?.name;

  // Fetch transcript data using fiscal year/quarter
  const {
    data: transcriptData,
    isLoading: isLoadingTranscript,
    error: transcriptError,
  } = useEarningsTranscript(symbol, fiscalYearStr, fiscalQuarterStr);

  // Combine loading states
  const isLoading = isLoadingDates || isLoadingTranscript;
  const error = datesError || transcriptError;

  const transcript = transcriptData?.[0] || null;

  // If we have transcript dates but the current selection doesn't exist,
  // default to the most recent available quarter
  const shouldAutoSelect = useMemo(() => {
    if (!quarterOptions.length) return false;

    const currentSelectionExists = quarterOptions.some(
      (option) => option.year === year && option.quarter === quarter
    );

    return !currentSelectionExists;
  }, [quarterOptions, year, quarter]);

  // Auto-select most recent quarter if current selection is invalid
  React.useEffect(() => {
    if (shouldAutoSelect && quarterOptions.length > 0) {
      const mostRecent = quarterOptions[0]; // First item is most recent
      onStateChange({
        year: mostRecent.year,
        quarter: mostRecent.quarter,
      });
    }
  }, [shouldAutoSelect, quarterOptions, onStateChange]);

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleFullscreenToggle = () => {
    setIsFullscreen((prev) => !prev);
  };

  // Use parent's quarter select handler

  const handleModeChange = (newMode: TranscriptMode) => {
    onStateChange({
      mode: newMode,
    });
  };

  return (
    <Box
      sx={{
        width: "100%",
        px: { xs: 2, sm: 3 },
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        ...(fillHeight && { pb: { xs: 2, sm: 3 } }),
      }}
    >
      <TranscriptDisplay
        transcript={transcript}
        symbol={symbol}
        companyName={companyName}
        isLoading={isLoading}
        error={error}
        year={year}
        quarter={quarter}
        fiscalYear={selectedTranscriptDate?.fiscalYear}
        fiscalQuarter={selectedTranscriptDate?.quarter}
        mode={mode}
        onModeChange={handleModeChange}
        quarterOptions={quarterOptions}
        selectedQuarter={selectedQuarter}
        onQuarterSelect={onQuarterSelect}
        isFullscreen={isFullscreen}
        onFullscreenToggle={handleFullscreenToggle}
        fillHeight={fillHeight}
      />
    </Box>
  );
};

export default ViewCompanyEarningsTranscript;
